import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Autocomplete, TextField, Typography, Chip } from '@mui/material';
import {
  NODE_UI,
  stopPropagation,
  getCanvasSurfaceProps,
  getDialogSurfaceZIndex,
  CANVAS_AUTOCOMPLETE_POPPER_SX,
} from './nodeUiStyles';

/**
 * NodeAutocomplete — MUI Autocomplete for searchable/filterable dropdowns.
 * Supports single, multiple, and freeSolo variants.
 *
 * Unlike NodePlaceholderAutocomplete (which inserts ${...} tokens),
 * this is for general-purpose option selection (e.g., selecting a workflow,
 * database table, timezone, etc.).
 *
 * Usage:
 *   <NodeAutocomplete
 *     label="Select Workflow"
 *     value={selectedWorkflow}
 *     onChange={(val) => setSelectedWorkflow(val)}
 *     options={workflows}
 *     getOptionLabel={(opt) => opt.name}
 *     placeholder="Search workflows..."
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) => prop !== 'isMultiple',
})(({ theme, isMultiple }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    padding: '2px 4px !important',
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.text.primary,
      borderWidth: '1px',
    },
    ...(isMultiple && {
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '4px',
      // Reserve space for the dropdown arrow so chips never sit underneath it.
      paddingRight: '30px !important',
    }),
  },
  ...(isMultiple && {
    // MUI vertically centers the end adornment; when chips wrap that X/arrow
    // overlaps the chip rows. Pin controls to the top-right instead.
    '& .MuiAutocomplete-endAdornment': {
      top: '6px',
      right: '4px',
      transform: 'none',
      alignSelf: 'flex-start',
    },
    '& .MuiAutocomplete-tag': {
      margin: '2px',
      maxWidth: 'calc(100% - 8px)',
    },
  }),
  '& .MuiInputBase-input': {
    padding: '4px 6px !important',
    fontSize: NODE_UI.fontSize.value,
    ...(isMultiple && {
      minWidth: '60px !important',
      flexGrow: 1,
    }),
  },
}));

const paperProps = {
  sx: (theme) => ({
    borderRadius: NODE_UI.borderRadiusSm,
    boxShadow: NODE_UI.shadow,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: '2px',
    '& .MuiAutocomplete-option': {
      fontSize: NODE_UI.fontSize.value,
      padding: '6px 12px',
    },
  }),
};

const NodeAutocomplete = React.memo(function NodeAutocomplete({
  label,
  value,
  onChange,
  options = [],
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  placeholder,
  disabled = false,
  fullWidth = true,
  freeSolo = false,
  multiple = false,
  loading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
  filterOptions,
  groupBy,
  renderGroup,
  disableCloseOnSelect,
  disableClearable = false,
  sx,
  /** See NodeSelect `canvas` — portaled listbox with nodrag/nopan on React Flow. */
  canvas = false,
  /** Raise portaled listbox above MUI Dialog (required when dialog uses zIndex > modal). */
  inDialog = false,
}) {
  const handleChange = useCallback((_e, newValue) => {
    onChange?.(newValue);
  }, [onChange]);
  const handleOpen = useCallback((e) => {
    e.stopPropagation();
  }, []);
  const canvasSurfaceProps = getCanvasSurfaceProps(canvas);

  return (
    <div
      className={canvas ? 'nodrag nopan nowheel' : undefined}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {label && <Label>{label}</Label>}
      <StyledAutocomplete
        isMultiple={multiple}
        value={value ?? (multiple ? [] : null)}
        onChange={handleChange}
        onOpen={handleOpen}
        disableClearable={disableClearable}
        options={options}
        getOptionLabel={getOptionLabel || ((opt) => {
          if (typeof opt === 'string') return opt;
          return opt?.label || opt?.name || '';
        })}
        isOptionEqualToValue={isOptionEqualToValue || ((opt, val) => {
          if (typeof opt === 'string' && typeof val === 'string') return opt === val;
          return opt?.value === val?.value || opt?.id === val?.id;
        })}
        renderOption={renderOption}
        freeSolo={freeSolo}
        multiple={multiple}
        disabled={disabled}
        fullWidth={fullWidth}
        loading={loading}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        filterOptions={filterOptions}
        groupBy={groupBy}
        renderGroup={renderGroup}
        disableCloseOnSelect={disableCloseOnSelect}
        componentsProps={{
          popper: {
            ...canvasSurfaceProps,
            ...(canvas
              ? {
                  style: { zIndex: 1500 },
                  sx: CANVAS_AUTOCOMPLETE_POPPER_SX,
                }
              : {}),
            ...(inDialog
              ? {
                  sx: (theme) => ({
                    zIndex: getDialogSurfaceZIndex(theme),
                    ...(canvas ? CANVAS_AUTOCOMPLETE_POPPER_SX : {}),
                  }),
                }
              : {}),
          },
          paper: canvas
            ? {
                ...canvasSurfaceProps,
                sx: (theme) => ({
                  ...paperProps.sx(theme),
                  maxWidth: CANVAS_AUTOCOMPLETE_POPPER_SX.maxWidth,
                }),
              }
            : paperProps,
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={placeholder}
          />
        )}
        renderTags={multiple ? (tagValues, getTagProps) =>
          tagValues.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={typeof option === 'string' ? option : option?.id || option?.value || index}
              label={typeof option === 'string' ? option : getOptionLabel?.(option) || option?.label || option?.name || ''}
              size="small"
              sx={{
                fontSize: NODE_UI.fontSize.caption,
                height: '22px',
                borderRadius: NODE_UI.borderRadiusSm,
                maxWidth: '100%',
                '& .MuiChip-label': {
                  paddingRight: '4px',
                },
                '& .MuiChip-deleteIcon': {
                  margin: '0 2px 0 -2px',
                  flexShrink: 0,
                },
              }}
            />
          )) : undefined}
        sx={sx}
      />
    </div>
  );
});

export default NodeAutocomplete;
